import React from 'react';
import { BRAND } from '../config/brand';

interface MobileHeaderProps {
  onMenuToggle: () => void;
  isMenuOpen: boolean;
}

/**
 * MobileHeader — Fixed top bar rendered on mobile viewports (< 768px).
 * Contains the brand logo and a hamburger/close menu toggle button.
 * Height: 56px (matches --mobile-header-height CSS variable).
 */
export function MobileHeader({ onMenuToggle, isMenuOpen }: MobileHeaderProps) {
  return (
    <header style={styles.header}>
      <a
        href={BRAND.marketingUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ ...styles.brand, textDecoration: 'none' }}
        title={`Go to ${BRAND.marketingUrl.replace('https://', '')}`}
        aria-label={`Open ${BRAND.name} marketing site in a new tab`}
      >
        <img
          src={BRAND.markWhite}
          alt=""
          aria-hidden="true"
          style={{ height: 28, width: 'auto', objectFit: 'contain', flexShrink: 0, display: 'block' }}
        />
        <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em', color: '#ffffff' }}>
          {BRAND.name}
        </span>
      </a>

      <button
        style={styles.hamburgerButton}
        onClick={onMenuToggle}
        aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isMenuOpen}
      >
        <span style={styles.hamburgerIcon}>
          {isMenuOpen ? '✕' : '☰'}
        </span>
      </button>
    </header>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '56px',
    backgroundColor: '#0a1f14',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px',
    zIndex: 200,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    boxSizing: 'border-box',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logoImg: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    objectFit: 'contain' as const,
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: '2px',
  },
  brandTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#f0f4f1',
    lineHeight: '1.2',
  },
  brandSub: {
    fontSize: '10px',
    color: '#c9a84c',
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  hamburgerButton: {
    minWidth: '44px',
    minHeight: '44px',
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    cursor: 'pointer',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  hamburgerIcon: {
    fontSize: '22px',
    color: '#f0f4f1',
    lineHeight: 1,
  },
};
